import { Navigate, useLocation } from 'react-router-dom';
import { useAccessToken } from 'providers/AccessTokenProvider';
import Error403 from 'pages/error/Error403';
import { routes } from 'sitemap';

interface ProtectedRouteProps {
  children: JSX.Element;
  requiredPermission?: string;
  adminOnly?: boolean;
}

const findPermissionForPath = (path: string): string | undefined => {
  const normalizedPath = path.endsWith('/') ? path.slice(0, -1) : path;

  for (const routeItem of routes) {
    for (const page of routeItem.pages) {
      if (page.path === normalizedPath) {
        return page.permission;
      }

      if (page.pages) {
        for (const subPage of page.pages) {
          if (subPage.path === normalizedPath) {
            return subPage.permission;
          }
        }
      }
    }
  }

  return undefined;
};

const ProtectedRoute = ({
  children,
  requiredPermission,
  adminOnly = false
}: ProtectedRouteProps) => {
  const { authUser, hasPermission, isAdmin, isSessionLoading } =
    useAccessToken();
  const location = useLocation();

  if (isSessionLoading) {
    return null;
  }

  if (!authUser) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Error403 />;
  }

  if (isAdmin) {
    return children;
  }

  const permissionToCheck =
    requiredPermission || findPermissionForPath(location.pathname);
  if (permissionToCheck && !hasPermission(permissionToCheck)) {
    return <Error403 />;
  }

  return children;
};

export default ProtectedRoute;
